import { useId } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useSearch } from '@tanstack/react-router';
import { Settings } from 'lucide-react';
import { portalQueries } from '@blimo/api-client';
import { useApi, useMe } from '@blimo/api-client/react';
import { hasPermission, OP_TYPE_LABELS, OP_TYPES, VAT_BASIS_OPTIONS } from '@blimo/shared';
import { Button, ErrorState, Label, Select, Skeleton, ToggleGroup } from '@blimo/ui';
import { AccumulatedDevelopmentView, CostsView, MonthRangePicker, OpenItemsView, RentDevelopmentView } from '@blimo/ui/finance';
import { ForbiddenPage } from './pages.jsx';
import { useFinanceSearch } from '../lib/finance-search.js';
import { PageHeader } from '../components/ui/page-header.jsx';

/** Finance pages need `finance.view` (investors); the API answers 403 otherwise. */
function useCanViewFinance() {
  const { data: me } = useMe();
  return hasPermission(me, 'finance.view');
}

function FinanceSkeleton() {
  return (
    <div className="flex flex-col gap-3" aria-busy="true" aria-label="Wird geladen">
      <Skeleton className="h-56 w-full" />
      <Skeleton className="h-40 w-full" />
    </div>
  );
}

/** Wraps a finance query result: error → ErrorState, pending → skeleton, refetch → keeps the frame dimmed. */
function QueryFrame({ query, children }) {
  if (query.error) return <ErrorState error={query.error} onRetry={() => query.refetch()} />;
  if (query.isPending) return <FinanceSkeleton />;
  return <div className={query.isPlaceholderData ? 'opacity-60 transition-opacity' : undefined}>{children(query.data)}</div>;
}

/**
 * Labelled native select of properties ("Alle Liegenschaften" = empty).
 * @param {{ value?: string, onChange: (value: string|undefined) => void, properties: Array<{ id: number|string, name?: string|null }>, label?: string, allLabel?: string|null }} props
 */
export function PropertySelect({ value, onChange, properties, label = 'Liegenschaft', allLabel = 'Alle Liegenschaften' }) {
  const id = useId();
  return (
    <div className="flex flex-col gap-1">
      <Label htmlFor={id} className="text-xs! text-muted-foreground">
        {label}
      </Label>
      <Select id={id} value={value ?? ''} onChange={(e) => onChange(e.target.value || undefined)} wrapperClassName="sm:w-64">
        {allLabel !== null && <option value="">{allLabel}</option>}
        {properties.map((p) => (
          <option key={p.id} value={String(p.id)}>
            {p.name || `Liegenschaft #${p.id}`}
          </option>
        ))}
      </Select>
    </div>
  );
}

/** Period plus the property filter – the key is only added when set (stable query keys). */
const withProperty = (period, property) => (property ? { ...period, property } : period);

/* ------------------------------------------------------------------ Entwicklung */

export function DevelopmentPage() {
  const api = useApi();
  const { data: me } = useMe();
  const canView = useCanViewFinance();
  const search = useSearch({ strict: false });
  const { period, update } = useFinanceSearch(search);
  // `property` is filtered by the API; the options come from the user's properties.
  const query = useQuery({ ...portalQueries.financeRentDevelopment(api, withProperty(period, search.property)), enabled: canView });
  const propertyList = useQuery({ ...portalQueries.properties(api), enabled: canView });
  if (!canView) return <ForbiddenPage />;

  const properties = propertyList.data ?? [];
  const reported = query.data?.properties ?? [];
  const accumulatedId = search.property ?? (reported.length > 0 ? String(reported[0].id) : undefined);

  return (
    <div className="flex flex-col gap-6">
      <PageHeader title="Entwicklung" description="Mietentwicklung und Bruttomietrendite Ihrer Liegenschaften je Monat." />
      <div className="flex flex-col gap-3 rounded-xl border bg-card p-3 sm:flex-row sm:px-4 sm:flex-wrap sm:items-end">
        <MonthRangePicker from={period.from} to={period.to} onChange={(range) => update(range)} />
        {properties.length > 1 && <PropertySelect value={search.property} onChange={(property) => update({ property })} properties={properties} />}
      </div>
      <QueryFrame query={query}>{(data) => <RentDevelopmentView data={data} propertyId={search.property} />}</QueryFrame>

      {accumulatedId && (
        <section aria-labelledby="accumulated-heading" className="flex flex-col gap-3 pt-2">
          <div className="flex flex-col gap-0.5">
            <h2 id="accumulated-heading" className="text-base font-semibold">
              Kumulierte Entwicklung seit Kauf
            </h2>
            <p className="text-[0.8125rem] text-muted-foreground">
              {[...reported, ...properties].find((p) => String(p.id) === accumulatedId)?.name ?? ''} · Jahreswerte seit dem Kaufjahr
              {!search.property && properties.length > 1 ? ' – andere Liegenschaft über den Filter oben wählen' : ''}
            </p>
          </div>
          <AccumulatedSection propertyId={accumulatedId} canManage={hasPermission(me, 'finance.baselines.manage')} />
        </section>
      )}
    </div>
  );
}

/** @param {{ propertyId: string, canManage: boolean }} props */
export function AccumulatedSection({ propertyId, canManage }) {
  const api = useApi();
  const query = useQuery(portalQueries.accumulatedDevelopment(api, propertyId));
  return (
    <QueryFrame query={query}>
      {(data) => (
        <AccumulatedDevelopmentView
          data={data}
          action={
            canManage && (
              <Button asChild variant="outline" size="sm" className="w-fit">
                <Link to="/immobilien/$propertyId" params={{ propertyId }} search={{ tab: 'einstellungen' }}>
                  <Settings aria-hidden="true" /> Zu den Einstellungen
                </Link>
              </Button>
            )
          }
        />
      )}
    </QueryFrame>
  );
}

/* ------------------------------------------------------------------ Offene Posten */

const OP_TYPE_OPTIONS = [{ value: '', label: 'Alle' }, ...OP_TYPES.map((t) => ({ value: t, label: OP_TYPE_LABELS[t] }))];

/** @param {{ value?: string, onChange: (value: string|undefined) => void }} props */
export function OpTypeFilter({ value, onChange }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs font-medium text-muted-foreground">Art</span>
      <ToggleGroup
        label="Art der offenen Posten"
        size="default"
        value={value ?? ''}
        onValueChange={(v) => onChange(v || undefined)}
        options={OP_TYPE_OPTIONS}
        className="max-w-full overflow-x-auto"
      />
    </div>
  );
}

export function OpenItemsPage() {
  const api = useApi();
  const canView = useCanViewFinance();
  const search = useSearch({ strict: false });
  const { update } = useFinanceSearch(search);
  const params = { op_type: search.op_type, property: search.property };
  const query = useQuery({ ...portalQueries.financeOpenItems(api, params), enabled: canView });
  const properties = useQuery({ ...portalQueries.properties(api), enabled: canView });
  if (!canView) return <ForbiddenPage />;

  return (
    <div className="flex flex-col gap-6">
      <PageHeader title="Offene Posten" description="Offene Posten der Mieterkonten Ihrer Einheiten. Negative Beträge sind Rückstände." />
      <div className="flex flex-col gap-3 rounded-xl border bg-card p-3 sm:flex-row sm:px-4 sm:flex-wrap sm:items-end">
        <OpTypeFilter value={search.op_type} onChange={(op_type) => update({ op_type })} />
        {(properties.data?.length ?? 0) > 1 && (
          <PropertySelect value={search.property} onChange={(property) => update({ property })} properties={properties.data} />
        )}
      </div>
      <QueryFrame query={query}>{(data) => <OpenItemsView data={data} />}</QueryFrame>
    </div>
  );
}

/* ------------------------------------------------------------------ Kosten */

/**
 * Betragsbasis (G-20): Brutto = wie gebucht (Standard), Netto = MwSt je Buchung herausgerechnet – Berechnung in der API.
 * @param {{ value?: string, onChange: (value: string|undefined) => void, label?: string, fallback?: string }} props
 */
export function VatToggle({ value, onChange, label = 'Beträge', fallback = 'gross' }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      <ToggleGroup
        label="Betragsbasis"
        size="default"
        value={value ?? fallback}
        onValueChange={(v) => onChange(v === fallback ? undefined : v)}
        options={VAT_BASIS_OPTIONS}
      />
    </div>
  );
}

export function CostsPage() {
  const api = useApi();
  const canView = useCanViewFinance();
  const search = useSearch({ strict: false });
  const { period, update } = useFinanceSearch(search);
  const params = { ...withProperty(period, search.property), ...(search.vat === 'net' ? { vat: 'net' } : {}) };
  const query = useQuery({ ...portalQueries.financeCosts(api, params), enabled: canView });
  const properties = useQuery({ ...portalQueries.properties(api), enabled: canView });
  if (!canView) return <ForbiddenPage />;

  return (
    <div className="flex flex-col gap-6">
      <PageHeader title="Kosten" description="Kostenbuchungen der für Sie freigegebenen Konten – Monatswerte und laufende Summe ab Beginn des Zeitraums." />
      <div className="flex flex-col gap-3 rounded-xl border bg-card p-3 sm:flex-row sm:px-4 sm:flex-wrap sm:items-end">
        <MonthRangePicker from={period.from} to={period.to} onChange={(range) => update(range)} />
        {(properties.data?.length ?? 0) > 1 && (
          <PropertySelect value={search.property} onChange={(property) => update({ property })} properties={properties.data} />
        )}
        <VatToggle value={search.vat} onChange={(vat) => update({ vat })} />
      </div>
      <QueryFrame query={query}>
        {(data) => (
          <div className="flex flex-col gap-2">
            <p className="text-sm text-muted-foreground" data-testid="vat-basis">
              {data.vat_basis === 'net' ? 'Nettobeträge – MwSt je Buchung mit ihrem Steuersatz herausgerechnet.' : 'Bruttobeträge wie gebucht (inkl. MwSt).'}
            </p>
            <CostsView data={data} />
          </div>
        )}
      </QueryFrame>
    </div>
  );
}
